import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, CircleCheck, RotateCcw, UserPlus } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import type { TicketDetail, UpdateTicketInput } from '@/api/ticketsApi'
import { ApiError } from '@/api/request'
import { AssigneeAvatar, AssigneeOptions } from '@/components/board/AssigneePicker'
import { errorMessage } from '@/components/common/States'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAgents } from '@/hooks/useDirectory'
import { ticketKey, useUpdateTicket } from '@/hooks/useTickets'
import { statusOptions, type StatusOption } from '@/lib/ticketDetails'
import { PRIORITIES, priorityDotClass, priorityLabel, priorityPillClass, statusLabel, statusPillClass } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/ToastProvider'

/**
 * Status, priority and assignee as one-click controls (the ticket header and
 * the details rail), as in Linear or Jira: the pill is the menu. Every change
 * is the same If-Match PATCH as before; resolve and reopen open their dialogs,
 * which ask for the reason (and the GxP assessment).
 */

const pillBase = 'inline-flex items-center gap-1 rounded-sm px-2 py-[3px] text-label'
const menuItem =
  'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-left text-meta text-ink-body hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'
const menuHeading = 'px-2.5 pb-1 pt-2 text-eyebrow font-medium uppercase text-ink-muted'

/** A ticket write with the rail's conflict handling: a stale version refetches and says so. */
export function useQuickSave(ticket: TicketDetail, routeId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const update = useUpdateTicket(routeId)
  const save = (input: UpdateTicketInput, done?: () => void) =>
    update.mutate(
      { version: ticket.version, input },
      {
        onSuccess: () => done?.(),
        onError: (err) => {
          if (err instanceof ApiError && (err.status === 409 || err.status === 412)) {
            void queryClient.invalidateQueries({ queryKey: ticketKey(routeId) })
            notify({ title: t('ticket.rail.conflict'), tone: 'warning' })
          } else notify({ title: t('ticket.rail.saveError', { message: errorMessage(err) }), tone: 'error' })
        },
      },
    )
  return { save, pending: update.isPending }
}

function Trigger({ label, className, children }: { label: string; className: string; children: ReactNode }) {
  return (
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={label}
        title={label}
        className={cn(pillBase, 'transition-shadow hover:ring-2 hover:ring-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:ring-2 data-[state=open]:ring-primary/40', className)}
      >
        {children}
        <ChevronDown className="size-3 opacity-70" aria-hidden />
      </button>
    </PopoverTrigger>
  )
}

/** The status pill as a menu of every status: one click to move, greyed with why when it can't. */
export function StatusMenu({
  ticket,
  routeId,
  can,
  onResolve,
  onReopen,
}: {
  ticket: TicketDetail
  routeId: string
  can: { update: boolean; resolve: boolean; reopen: boolean }
  onResolve: () => void
  onReopen: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { save, pending } = useQuickSave(ticket, routeId)
  const options = statusOptions(ticket.status, ticket.allowedTransitions, can)
  const anyAction = options.some((o) => o.kind === 'move' || o.kind === 'resolve' || o.kind === 'reopen')
  const pill = cn('font-medium', statusPillClass(ticket.status))
  if (!anyAction) return <span className={cn(pillBase, pill)}>{statusLabel(ticket.status)}</span>

  const pick = (o: StatusOption) => {
    setOpen(false)
    if (o.kind === 'move') save({ status: o.status })
    else if (o.kind === 'resolve') onResolve()
    else if (o.kind === 'reopen') onReopen()
  }
  const hint = (o: StatusOption) =>
    o.kind === 'resolve'
      ? t('ticket.quick.resolveHint')
      : o.kind === 'reopen'
        ? t('ticket.quick.reopenHint')
        : o.kind === 'blocked'
          ? t(`ticket.quick.blocked.${o.reason}`, { from: statusLabel(ticket.status) })
          : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Trigger label={t('ticket.rail.editStatus')} className={pill}>
        {statusLabel(ticket.status)}
      </Trigger>
      <PopoverContent align="start" className="w-72 p-1">
        <p className={menuHeading}>{t('ticket.quick.moveTo')}</p>
        {options.map((o) => {
          const h = hint(o)
          return (
            <button
              key={o.status}
              type="button"
              disabled={pending || o.kind === 'blocked' || o.kind === 'current'}
              aria-current={o.kind === 'current' ? true : undefined}
              title={h ?? undefined}
              onClick={() => pick(o)}
              className={cn(menuItem, o.kind === 'current' && 'disabled:opacity-100')}
            >
              <span aria-hidden className={cn('size-2.5 shrink-0 rounded-[3px]', statusPillClass(o.status))} />
              <span className="flex min-w-0 grow flex-col">
                <span className={cn('truncate', o.kind === 'current' && 'font-semibold text-ink')}>{statusLabel(o.status)}</span>
                {h && o.kind !== 'blocked' ? <span className="truncate text-label text-ink-muted">{h}</span> : null}
              </span>
              {o.kind === 'current' ? <Check className="size-3.5 shrink-0 text-primary" aria-hidden /> : null}
              {o.kind === 'resolve' ? <CircleCheck className="size-3.5 shrink-0 text-ink-muted" aria-hidden /> : null}
              {o.kind === 'reopen' ? <RotateCcw className="size-3.5 shrink-0 text-ink-muted" aria-hidden /> : null}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

/** The priority pill as a P1–P4 menu. */
export function PriorityMenu({ ticket, routeId, canUpdate }: { ticket: TicketDetail; routeId: string; canUpdate: boolean }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { save, pending } = useQuickSave(ticket, routeId)
  const done = ticket.status === 'resolved' || ticket.status === 'closed'
  const pill = cn('font-semibold', done ? 'bg-status-neutral-bg text-status-neutral-fg' : priorityPillClass(ticket.priority))
  if (!canUpdate || done) return <span className={cn(pillBase, pill)}>{priorityLabel(ticket.priority)}</span>
  const current = ticket.priority.toLowerCase()
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Trigger label={t('ticket.quick.changePriority')} className={pill}>
        {priorityLabel(ticket.priority)}
      </Trigger>
      <PopoverContent align="start" className="w-56 p-1">
        <p className={menuHeading}>{t('ticket.quick.priority')}</p>
        {PRIORITIES.map((p) => (
          <button
            key={p}
            type="button"
            disabled={pending || p === current}
            aria-current={p === current ? true : undefined}
            onClick={() => {
              setOpen(false)
              save({ priority: p.toUpperCase() })
            }}
            className={cn(menuItem, p === current && 'disabled:opacity-100')}
          >
            <span aria-hidden className={cn('size-2.5 shrink-0 rounded-full', priorityDotClass(p))} />
            <span className={cn('grow', p === current && 'font-semibold text-ink')}>{priorityLabel(p)}</span>
            {p === current ? <Check className="size-3.5 shrink-0 text-primary" aria-hidden /> : null}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

/** Who has the ticket, as a picker: Assign to me, Unassign, or search the team. */
export function AssigneeMenu({ ticket, routeId, canAssign }: { ticket: TicketDetail; routeId: string; canAssign: boolean }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const agents = useAgents(canAssign)
  const { save, pending } = useQuickSave(ticket, routeId)
  const who = ticket.assignee
  const face = who ? (
    <AssigneeAvatar person={who} size="sm" />
  ) : (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed border-line-strong text-ink-muted">
      <UserPlus className="size-3" aria-hidden />
    </span>
  )
  const name = <span className={cn('min-w-0 truncate text-meta', who ? 'text-ink-strong' : 'text-ink-muted')}>{who?.name ?? t('ticket.rail.unassigned')}</span>
  if (!canAssign)
    return (
      <span className="flex min-w-0 items-center gap-2">
        {who ? face : null}
        {name}
      </span>
    )
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={pending}
          aria-label={t('ticket.rail.editAssignee')}
          title={t('ticket.rail.editAssignee')}
          className="-mx-1.5 -my-1 flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-surface-subtle"
        >
          {face}
          {name}
          <ChevronDown className="size-3 shrink-0 text-ink-muted" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-80 w-64 overflow-y-auto p-1">
        <p className={menuHeading}>{t('boards.card.assign')}</p>
        <AssigneeOptions
          assigneeId={who?.id ?? null}
          agents={agents.data ?? []}
          meId={user?.id ?? null}
          onPick={(id) => {
            setOpen(false)
            if (id !== (who?.id ?? null)) save({ assigneeId: id })
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
